import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { Volume2, Square } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

// Piccoli pulsanti "ascolta" iniettati in alto a destra di ogni <section> della pagina,
// senza toccare il markup delle sezioni: ogni pulsante legge ad alta voce solo quella sezione.
export default function SectionListenButtons() {
  const { t, locale } = useLang();
  const { pathname } = useLocation();
  const [targets, setTargets] = useState([]); // [{ id, holder, section }]
  const [activeId, setActiveId] = useState(null);
  const activeRef = useRef(null);
  activeRef.current = activeId;

  // il pannello accessibilità che avvia la lettura globale spegne i bottoni di sezione
  useEffect(() => {
    const stop = () => setActiveId(null);
    window.addEventListener("gb-tts-global-start", stop);
    return () => window.removeEventListener("gb-tts-global-start", stop);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const mine = [];

    const setup = () => {
      if (cancelled) return;
      const fresh = [];
      document.querySelectorAll("main section").forEach((section, i) => {
        if (section.querySelector("[data-listen-holder]")) return;
        if (getComputedStyle(section).position === "static") section.style.position = "relative";
        const holder = document.createElement("div");
        holder.dataset.listenHolder = "1";
        holder.className = i === 0
          ? "absolute top-20 right-3 sm:right-5 z-30"
          : "absolute top-3 right-3 sm:right-5 z-30";
        section.appendChild(holder);
        const entry = { id: section.id || `sezione-${i}`, holder, section };
        mine.push(entry);
        fresh.push(entry);
      });
      if (fresh.length) setTargets((prev) => [...prev, ...fresh]);
    };

    const t1 = setTimeout(setup, 600);
    const t2 = setTimeout(setup, 1800);
    return () => {
      cancelled = true;
      clearTimeout(t1);
      clearTimeout(t2);
      mine.forEach((m) => m.holder.remove());
      setTargets([]);
      window.speechSynthesis?.cancel();
    };
  }, [pathname]);

  const speakSection = (target) => {
    if (!("speechSynthesis" in window)) return;
    if (activeRef.current === target.id) {
      window.speechSynthesis.cancel();
      setActiveId(null);
      return;
    }
    // ferma un'eventuale lettura globale in corso (sincronizza anche il widget)
    window.dispatchEvent(new Event("gb-tts-section-start"));
    window.speechSynthesis.cancel();
    const text = (target.section.innerText || "").replace(/\n{2,}/g, ". ").replace(/\n/g, ". ").trim();
    if (text.length < 10) return;
    const u = new SpeechSynthesisUtterance(text);
    u.lang = locale;
    u.rate = 1;
    const clear = () => setActiveId((cur) => (cur === target.id ? null : cur));
    u.onend = clear;
    u.onerror = clear;
    window.speechSynthesis.speak(u);
    setActiveId(target.id);
  };

  return (
    <>
      {targets.map((target) =>
        createPortal(
          <button
            type="button"
            data-testid={`section-listen-${target.id}`}
            aria-label={t("a11y.listenSection")}
            aria-pressed={activeId === target.id}
            onClick={() => speakSection(target)}
            className={`flex h-9 w-9 items-center justify-center rounded-full border backdrop-blur-sm transition-all duration-300 focus:outline-none ${
              activeId === target.id
                ? "border-sky-400 bg-sky-400/25 text-sky-300 opacity-100"
                : "border-slate-700/70 bg-[#0C1220]/70 text-slate-400 opacity-60 hover:opacity-100 hover:border-sky-400/60 hover:text-sky-300 focus-visible:opacity-100"
            }`}
          >
            {activeId === target.id ? <Square className="h-3.5 w-3.5" /> : <Volume2 className="h-4 w-4" />}
          </button>,
          target.holder
        )
      )}
    </>
  );
}
